import { useState } from 'react';
import type { ScheduleOwnerDto } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';

/**
 * Plan 02-06 Task 3 — Admin SPA "Created in chat" badge (D-26 / D-27 / D-28).
 *
 * Renders adjacent to the schedule name when `row.createdBySource === 'copilot'`;
 * renders nothing for admin-created rows. Uses the existing shadcn
 * `<Badge variant="secondary">` primitive verbatim (no new shadcn block
 * added, per the plan's anti-pattern guard + RESEARCH.md note A8).
 *
 * The tooltip is a tiny self-contained CSS+aria component rather than a
 * Radix Tooltip — adding `@radix-ui/react-tooltip` would be "adding a new
 * shadcn block" which the plan explicitly forbids. The implementation
 * uses `role="tooltip"` on a hover/focus-revealed span so Playwright's
 * `page.getByRole('tooltip')` pattern (UI-SPEC line 795) works without
 * adding a dependency.
 *
 * Tooltip copy is D-26 verbatim (polished punctuation per UI-SPEC line 621).
 */
export const CREATED_IN_CHAT_TOOLTIP =
  'This schedule was created through the Copilot chat. Edit, pause, and delete work the same as admin-created schedules.';

const CREATED_IN_CHAT_LABEL = 'Created in chat';

export interface CreatedInChatBadgeProps {
  createdBySource: ScheduleOwnerDto['createdBySource'];
}

/**
 * Renders the badge conditionally. Returns `null` when the row was not
 * created via the Copilot chat — the absence of the element is what
 * Playwright's `toHaveCount(0)` assertion in UI-SPEC line 792 targets.
 */
export function CreatedInChatBadge({ createdBySource }: CreatedInChatBadgeProps) {
  const [visible, setVisible] = useState(false);
  if (createdBySource !== 'copilot') return null;
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <Badge
        variant="secondary"
        data-testid="created-in-chat-badge"
        // Native `title` attribute as a baseline a11y affordance for
        // browsers without JS / when the React state hasn't ticked yet.
        title={CREATED_IN_CHAT_TOOLTIP}
        tabIndex={0}
      >
        {CREATED_IN_CHAT_LABEL}
      </Badge>
      <span
        role="tooltip"
        aria-hidden={!visible}
        className={[
          'pointer-events-none absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 whitespace-normal rounded-md border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md',
          'max-w-xs',
          visible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        {CREATED_IN_CHAT_TOOLTIP}
      </span>
    </span>
  );
}
